import {
  Box,
  CircularProgress,
  ClickAwayListener,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Paper,
  Popper,
} from "@mui/material";
import { Icon, TextField, Typography } from "@platform/design-system";
import {
  KeyboardEvent,
  ReactElement,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { submitEvent } from "@platform/browser-logger";
import { useDebounce } from "@platform/hooks";
import { RFP_STATUSES, ROUTE_NAMES } from "@platform/types";
import { useNavigate } from "react-router";

import {
  ClientSearchHit,
  RfpSearchHit,
  SearchMatchField,
  isSearchableTerm,
  normalizeSearchTerm,
  useGlobalSearch,
} from "./searchApi";

/** Matches the RFP list's search debounce so both feel the same. */
export const GLOBAL_SEARCH_DEBOUNCE_MS = 300;

const SEARCH_WIDTH = "17.5rem";

type OptionKind = "client" | "rfp" | "seeAllClients" | "seeAllRfps";

interface SearchOption {
  readonly key: string;
  readonly kind: OptionKind;
  readonly to: string;
  readonly primary: string;
  readonly secondary?: string;
  readonly matchedOn?: readonly SearchMatchField[];
}

interface SearchSection {
  readonly label: string;
  readonly options: readonly SearchOption[];
}

// Date-only values carry no zone and render as-is; an explicit UTC ("Z")
// timestamp is rendered in UTC so the day doesn't shift with the viewer's zone.
const formatEffectiveDate = (
  date: string | null | undefined,
): string | undefined =>
  date
    ? new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        ...(date.endsWith("Z") && { timeZone: "UTC" }),
      })
    : undefined;

const isKnownRfpStatus = (
  status: string,
): status is keyof typeof RFP_STATUSES => status in RFP_STATUSES;

const rfpStatusLabel = (status: string): string =>
  isKnownRfpStatus(status) ? RFP_STATUSES[status] : status;

const seeAllPath = (route: string, term: string): string =>
  `/${route}?q=${encodeURIComponent(term)}`;

// undefined (not "") when every part is empty: ListItemText treats "" as a
// present secondary and pads the row for a line that never renders.
const joinDetails = (
  parts: readonly (string | undefined)[],
): string | undefined => {
  const details = parts.filter((part): part is string => Boolean(part));

  return details.length > 0 ? details.join(" · ") : undefined;
};

const toClientOption = (
  hit: ClientSearchHit,
  showBrokerName: boolean,
): SearchOption => ({
  key: `client-${hit.id}`,
  kind: "client",
  to: `/${ROUTE_NAMES.CLIENTS}/${hit.numericId}`,
  primary: hit.name,
  secondary: joinDetails([
    showBrokerName ? hit.brokerName : undefined,
    hit.matchedOn.includes("externalId") ? `ID ${hit.externalId}` : undefined,
  ]),
  matchedOn: hit.matchedOn,
});

const toRfpOption = (hit: RfpSearchHit): SearchOption => ({
  key: `rfp-${hit.id}`,
  kind: "rfp",
  to: `/${ROUTE_NAMES.RFPS}/${hit.id}`,
  primary: `RFP ${hit.id} · ${hit.clientName}`,
  secondary: joinDetails([
    formatEffectiveDate(hit.effectiveDate),
    hit.status ? rfpStatusLabel(hit.status) : undefined,
    // The analyst is why this row matched, so show it even though the
    // grid columns would not.
    hit.matchedOn.includes("analyst") && hit.assignedAnalyst
      ? `Analyst ${hit.assignedAnalyst}`
      : undefined,
  ]),
  matchedOn: hit.matchedOn,
});

const seeAllOption = (
  kind: "seeAllClients" | "seeAllRfps",
  route: string,
  term: string,
  primary: string,
): SearchOption => ({
  key: kind,
  kind,
  to: seeAllPath(route, term),
  primary,
});

/** A group with hits ends in a "See all" row; a group without hits is dropped. */
const toSection = (
  label: string,
  options: readonly SearchOption[],
  seeAll: SearchOption,
): SearchSection => ({
  label,
  options: options.length > 0 ? [...options, seeAll] : [],
});

const buildSections = (
  term: string,
  clients: readonly ClientSearchHit[],
  rfps: readonly RfpSearchHit[],
  showBrokerName: boolean,
): readonly SearchSection[] =>
  [
    toSection(
      "RFPs",
      rfps.map(toRfpOption),
      seeAllOption("seeAllRfps", ROUTE_NAMES.RFPS, term, "See all RFPs"),
    ),
    toSection(
      "Clients",
      clients.map((hit) => toClientOption(hit, showBrokerName)),
      seeAllOption(
        "seeAllClients",
        ROUTE_NAMES.CLIENTS,
        term,
        "See all clients",
      ),
    ),
  ].filter((section) => section.options.length > 0);

interface GlobalSearchProps {
  /** Agents are scoped to one brokerage, so rows omit the broker name. */
  readonly isAgent: boolean;
  /** Overridable so tests do not wait on the real debounce. */
  readonly debounceMs?: number;
}

/**
 * Header typeahead over GET /bp/v1/search (ADR 10.36). Results are grouped
 * RFPs then Clients, arrow keys move the highlight, Enter opens the
 * highlighted row (or the first one), Escape closes. "See all" rows hand the
 * term to the list pages through `?q=`.
 */
export const GlobalSearch = ({
  isAgent,
  debounceMs = GLOBAL_SEARCH_DEBOUNCE_MS,
}: GlobalSearchProps): ReactElement => {
  const navigate = useNavigate();
  const listboxId = useId();
  const anchorRef = useRef<HTMLDivElement>(null);
  // Agents are scoped to a single brokerage, so the broker name would
  // repeat on every row; hide it for them.
  const showBrokerName = !isAgent;

  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState<number | null>(null);

  const debouncedTerm = normalizeSearchTerm(useDebounce(value, debounceMs));
  const searchable = isSearchableTerm(debouncedTerm);
  const { data, isFetching, isError } = useGlobalSearch(debouncedTerm);

  useEffect(() => {
    if (searchable) {
      submitEvent("Global Search Performed", {
        termLength: debouncedTerm.length,
      });
    }
  }, [searchable, debouncedTerm]);

  const sections = searchable
    ? buildSections(
        debouncedTerm,
        data?.clients ?? [],
        data?.rfps ?? [],
        showBrokerName,
      )
    : [];
  const options = sections.flatMap((section) => section.options);
  const showResults = open && searchable;
  const showErrorState = showResults && isError;
  const showEmptyState =
    showResults && !isError && !isFetching && options.length === 0;

  const close = () => {
    setOpen(false);
    setHighlighted(null);
  };

  const openOption = (option: SearchOption) => {
    submitEvent("Global Search Result Opened", {
      kind: option.kind,
      matchedOn: option.matchedOn,
    });
    close();
    setValue("");
    navigate(option.to);
  };

  const moveHighlight = (step: 1 | -1) => {
    if (options.length === 0) return;

    setOpen(true);
    setHighlighted((current) => {
      if (current === null) return step === 1 ? 0 : options.length - 1;

      return (current + step + options.length) % options.length;
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        moveHighlight(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        moveHighlight(-1);
        break;
      case "Enter": {
        const option = options[highlighted ?? 0];
        if (showResults && option) {
          event.preventDefault();
          openOption(option);
        }
        break;
      }
      case "Escape":
        close();
        break;
      default:
        break;
    }
  };

  const optionId = (index: number) => `${listboxId}-option-${index}`;

  return (
    <ClickAwayListener onClickAway={close}>
      <Box ref={anchorRef} data-testid="global-search" width={SEARCH_WIDTH}>
        <TextField
          size="sm"
          fullWidth
          autoComplete="off"
          placeholder="Search clients and RFPs"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setOpen(true);
            setHighlighted(null);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Icon name="search" size="sm" />
                </InputAdornment>
              ),
              endAdornment:
                showResults && isFetching ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
            },
            htmlInput: {
              "data-testid": "global-search-input",
              role: "combobox",
              "aria-label": "Search clients and RFPs",
              "aria-autocomplete": "list",
              "aria-expanded": showResults,
              "aria-controls": listboxId,
              "aria-activedescendant":
                highlighted === null ? undefined : optionId(highlighted),
            },
          }}
        />
        <Popper
          open={showResults}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          sx={{ zIndex: (theme) => theme.zIndex.modal }}
        >
          <Paper
            elevation={4}
            data-testid="global-search-results"
            sx={{ width: SEARCH_WIDTH, maxHeight: "60vh", overflowY: "auto" }}
          >
            {showErrorState ? (
              <Typography
                variant="body-medium"
                color="textSecondary"
                data-testid="global-search-error"
                sx={{ p: 2 }}
              >
                Search isn’t available right now. Try again in a moment.
              </Typography>
            ) : showEmptyState ? (
              <Typography
                variant="body-medium"
                color="textSecondary"
                data-testid="global-search-empty"
                sx={{ p: 2 }}
              >
                No clients or RFPs match “{debouncedTerm}”
              </Typography>
            ) : (
              <List dense disablePadding role="listbox" id={listboxId}>
                {sections.map((section) => (
                  <li key={section.label}>
                    <ul style={{ padding: 0 }}>
                      <ListSubheader
                        disableSticky
                        // The group name anchors scanning, so it outweighs
                        // the rows: bold, full-contrast, a step larger than
                        // the muted 14px default.
                        sx={{
                          fontWeight: 700,
                          fontSize: "1rem",
                          color: "text.primary",
                        }}
                      >
                        {section.label}
                      </ListSubheader>
                      {section.options.map((option) => {
                        const index = options.indexOf(option);

                        return (
                          <ListItemButton
                            key={option.key}
                            id={optionId(index)}
                            role="option"
                            aria-selected={highlighted === index}
                            selected={highlighted === index}
                            data-testid={`global-search-option-${option.key}`}
                            onMouseEnter={() => setHighlighted(index)}
                            onClick={() => openOption(option)}
                          >
                            <ListItemText
                              primary={option.primary}
                              secondary={option.secondary}
                              slotProps={{
                                primary: { noWrap: true },
                                secondary: { noWrap: true },
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </ul>
                  </li>
                ))}
              </List>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
